import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { BarChart3, BookOpen, Users } from 'lucide-react'
import { useReducedMotion } from '@/components/scrollytelling'
import { FloatingNav } from '@/components/layout'
import { useRosterProfile } from '@/hooks/equip/useRosterProfile'

export function ParticipantLanding() {
  const reducedMotion = useReducedMotion()
  const { data: rosterProfile } = useRosterProfile()
  const isPhysician = rosterProfile?.isPhysician === true

  return (
    <div className="relative h-screen flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20 overflow-hidden">
      <FloatingNav
        showHome={false}
        items={[
          {
            id: 'performance',
            label: 'My Performance',
            path: '/performance',
            icon: <BarChart3 className="w-4 h-4" />,
          },
          {
            id: 'framework',
            label: 'Framework',
            path: '/framework',
            icon: <BookOpen className="w-4 h-4" />,
          },
          ...(isPhysician
            ? [{ id: 'apc-dashboard', label: 'My APCs', path: '/apc-dashboard', icon: <Users className="w-4 h-4" /> }]
            : []),
        ]}
      />
      {/* Background pattern */}
      {!reducedMotion && (
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(135, 123, 233, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(135, 123, 233, 0.3) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
        </div>
      )}

      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.div
          initial={reducedMotion ? {} : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6"
          >
            <span className="text-eventus-teal/80 text-sm font-medium tracking-widest uppercase">
              EQuIP 2026 BETA
            </span>
          </motion.div>

          <motion.h1
            initial={reducedMotion ? {} : { opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.6,
              delay: 0.3,
              type: 'spring',
              stiffness: 100,
            }}
            className="text-6xl sm:text-7xl md:text-8xl font-bold mb-6"
          >
            <span className="text-white">Your</span>
            <span className="bg-gradient-to-r from-eventus-teal to-eventus-teal-light bg-clip-text text-transparent">
              {' '}
              Performance
            </span>
          </motion.h1>

          <motion.p
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-8"
          >
            Track your clinical quality metrics in real-time. Understand your
            progress and see exactly where you're making an impact on patient
            outcomes.
          </motion.p>

          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-wrap gap-3 justify-center"
          >
            <Link
              to="/performance"
              className="inline-flex items-center gap-2 px-6 py-3 bg-eventus-teal hover:bg-eventus-teal-dark text-eventus-navy text-sm font-bold rounded-full transition-all hover:shadow-lg hover:-translate-y-0.5 border-2 border-eventus-teal"
            >
              <BarChart3 className="w-4 h-4" />
              View My Performance
            </Link>

            <Link
              to="/framework"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all hover:border-white/40"
            >
              <BookOpen className="w-4 h-4" />
              Learn the Framework
            </Link>

            {isPhysician && (
              <Link
                to="/apc-dashboard"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all hover:border-white/40"
              >
                <Users className="w-4 h-4" />
                View My APCs
              </Link>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
